import { InfoCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Position } from '@common';
import { PrimaryButton } from '@components/atoms';
import { Button, Col, Form, Input, Modal, Row, Select, Tag, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import './styles.css'; // Importando arquivo de estilo personalizado

const { Option } = Select;
const { TextArea } = Input;

interface AddJobPositionModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (position: Position) => void;
  initialValues?: Position;
}

export const AddJobPositionModal = ({ isOpen, onCancel, onSave, initialValues }: AddJobPositionModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showRequirementExamples, setShowRequirementExamples] = useState(false);
  const [showBenefitExamples, setShowBenefitExamples] = useState(false);
  const [customRequirement, setCustomRequirement] = useState('');
  const [customBenefit, setCustomBenefit] = useState('');

  useEffect(() => {
    if (isOpen) {
      form.resetFields();

      if (initialValues) {
        // Converter o valor booleano para string para o Select
        const formValues = {
          ...initialValues,
          featured: initialValues.featured ? "true" : "false"
        };
        form.setFieldsValue(formValues);
      }
    }
  }, [isOpen, initialValues, form]);

  // Exemplos de requisitos para quando o usuário adicionar um novo
  const requirementExamples = [
    "Minimum of 3 years of experience in a similar role",
    "Strong knowledge of safety protocols and procedures",
    "Excellent communication and teamwork skills",
    "Valid driver's license and ability to pass background checks",
    "Ability to work in various weather conditions and physically demanding environments"
  ];

  // Exemplos de benefícios para quando o usuário adicionar um novo
  const benefitExamples = [
    "Competitive salary and performance bonuses",
    "Comprehensive health, dental, and vision insurance",
    "401(k) retirement plan with company matching",
    "Paid time off and holidays",
    "Career development and training opportunities",
    "Employee recognition programs"
  ];

  const addSpecificRequirement = (requirement: string, add: (defaultValue?: any) => void) => {
    add(requirement);
    setShowRequirementExamples(false);
  };

  const addSpecificBenefit = (benefit: string, add: (defaultValue?: any) => void) => {
    add(benefit);
    setShowBenefitExamples(false);
  };

  const addCustomRequirement = (add: (defaultValue?: any) => void) => {
    if (customRequirement.trim()) {
      add(customRequirement);
      setCustomRequirement('');
    }
  };

  // Função para adicionar benefício personalizado
  const addCustomBenefit = (add: (defaultValue?: any) => void) => {
    if (customBenefit.trim()) {
      add(customBenefit);
      setCustomBenefit('');
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const id = initialValues?.id || values.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const datePosted = initialValues?.datePosted || (() => {
        const today = new Date();
        return `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
      })();

      // Garantir que requirements e benefits sejam sempre arrays, mesmo que vazios
      const requirements = Array.isArray(values.requirements) ? values.requirements : [];
      const benefits = Array.isArray(values.benefits) ? values.benefits : [];

      // Converter o valor do featured de string para booleano
      const featured = values.featured === "true";

      const newPosition: Position = {
        id,
        title: values.title,
        location: values.location,
        type: values.type,
        department: values.department,
        salaryRange: values.salaryRange,
        featured,
        datePosted,
        description: values.description,
        requirements,
        benefits
      };

      onSave(newPosition);
      form.resetFields();

    } catch (error) {
      console.error('Error validating form:', error);
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = initialValues
    ? <div className="!font-['Android_101'] !font-normal">EDIT POSITION</div>
    : <div className="!font-['Android_101'] !font-normal">ADD NEW POSITION</div>;

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      width={700}
      className="job-position-modal"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="title"
              label={<span className="text-white font-['Barlow']">POSITION TITLE</span>}
              rules={[{ required: true, message: 'Please enter the position title' }]}
            >
              <Input className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" placeholder="Ex: Heavy Equipment Operator" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="location"
              label={<span className="text-white font-['Barlow']">LOCATION</span>}
              rules={[{ required: true, message: 'Please enter the location' }]}
            >
              <Input className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" placeholder="Ex: Truckee, CA" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="type"
              label={<span className="text-white font-['Barlow']">CONTRACT TYPE</span>}
              rules={[{ required: true, message: 'Please select the contract type' }]}
            >
              <Select
                className="w-full !bg-second-black-carbon"
                dropdownClassName="!bg-second-black-carbon"
                style={{ backgroundColor: '#1e1e1e' }}
                popupClassName="!bg-second-black-carbon"
              >
                <Option value="Full-time">Full-time</Option>
                <Option value="Part-time">Part-time</Option>
                <Option value="Seasonal">Seasonal</Option>
                <Option value="Contract">Contract</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="department"
              label={<span className="text-white font-['Barlow']">DEPARTMENT</span>}
              rules={[{ required: true, message: 'Please select the department' }]}
            >
              <Select
                className="w-full !bg-second-black-carbon"
                dropdownClassName="!bg-second-black-carbon"
                style={{ backgroundColor: '#1e1e1e' }}
                popupClassName="!bg-second-black-carbon"
              >
                <Option value="Operations">Operations</Option>
                <Option value="Management">Management</Option>
                <Option value="Snow Removal">Snow Removal</Option>
                <Option value="Maintenance">Maintenance</Option>
                <Option value="Administration">Administration</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="salaryRange"
              label={<span className="text-white font-['Barlow']">SALARY RANGE</span>}
              rules={[{ required: true, message: 'Please enter the salary range' }]}
            >
              <Input className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" placeholder="Ex: $25-$35/hr DOE" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="featured"
              label={<span className="text-white font-['Barlow']">FEATURED</span>}
            >
              <Select
                className="w-full !bg-second-black-carbon"
                dropdownClassName="!bg-second-black-carbon"
                style={{ backgroundColor: '#1e1e1e' }}
                popupClassName="!bg-second-black-carbon"
              >
                <Option value="true">Yes</Option>
                <Option value="false">No</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="description"
              label={<span className="text-white font-['Barlow']">DESCRIPTION</span>}
              rules={[{ required: true, message: 'Please enter the position description' }]}
            >
              <TextArea
                rows={4}
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']"
                placeholder="Detailed position description"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Requirements Field - Dynamic List */}
        <Form.List name="requirements">
          {(fields, { add, remove }) => (
            <>
              <Form.Item
                label={
                  <div className="flex items-center">
                    <span className="text-white font-['Barlow'] mr-2">REQUIREMENTS</span>
                    <Tooltip title="Add job requirements such as experience, skills, certifications, etc.">
                      <InfoCircleOutlined className="text-gray-500" />
                    </Tooltip>
                  </div>
                }
              >
                <div className="bg-zinc-800 border border-zinc-700 p-4 mb-4 rounded">
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      className="flex items-center mb-3"
                    >
                      <div className="flex-grow">
                        <Form.Item
                          {...restField}
                          name={name}
                          className="mb-1"
                          rules={[{ required: true, message: 'Please enter requirement' }]}
                        >
                          <Input
                            placeholder="Add a job requirement"
                            className="bg-zinc-700 border-zinc-600 !text-white !font-['Barlow']"
                          />
                        </Form.Item>
                      </div>
                      <Button
                        type="text"
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                        className="text-[#ff4d4f] ml-2"
                      />
                    </div>
                  ))}

                  {/* Campo para adicionar requisito personalizado */}
                  <div className="flex items-center mb-4">
                    <Input
                      placeholder="Type a new requirement..."
                      value={customRequirement}
                      onChange={(e) => setCustomRequirement(e.target.value)}
                      onPressEnter={() => addCustomRequirement(add)}
                      className="bg-zinc-700 border-zinc-600 !text-white !font-['Barlow'] mr-2"
                    />
                    <PrimaryButton
                      type="primary"
                      icon={
                        <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="#000000" aria-hidden="true">
                          <path d="M482 152h60q8 0 8 8v704q0 8-8 8h-60q-8 0-8-8V160q0-8 8-8z"></path>
                          <path d="M192 474h672q8 0 8 8v60q0 8-8 8H160q-8 0-8-8v-60q0-8 8-8z"></path>
                        </svg>
                      }
                      onClick={() => addCustomRequirement(add)}
                      className="!bg-[#E3AF25] !text-black "
                      disabled={!customRequirement.trim()}
                    >
                      ADD
                    </PrimaryButton>
                  </div>

                  <div className="flex justify-between">
                    <PrimaryButton
                      type="primary"
                      onClick={() => setShowRequirementExamples(!showRequirementExamples)}
                      className="!bg-[#E3AF25] !text-black hover:!opacity-90 !border-none"
                    >
                      {showRequirementExamples ? 'Hide examples' : 'Show examples'}
                    </PrimaryButton>
                  </div>

                  {showRequirementExamples && (
                    <div className="mt-4 p-3 bg-zinc-700 rounded">
                      <div className="text-white mb-2 font-['Barlow']">Examples (click to add):</div>
                      <div className="flex flex-wrap gap-2">
                        {requirementExamples.map((example, idx) => (
                          <Tag
                            key={idx}
                            className="!bg-zinc-600 !text-white cursor-pointer !font-['Barlow'] mb-2"
                            onClick={() => addSpecificRequirement(example, add)}
                          >
                            {example}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Form.Item>
            </>
          )}
        </Form.List>

        {/* Benefits Field - Dynamic List */}
        <Form.List name="benefits">
          {(fields, { add, remove }) => (
            <>
              <Form.Item
                label={
                  <div className="flex items-center">
                    <span className="text-white font-['Barlow'] mr-2">BENEFITS</span>
                    <Tooltip title="Add job benefits such as salary, insurance, time off, etc.">
                      <InfoCircleOutlined className="text-gray-500" />
                    </Tooltip>
                  </div>
                }
              >
                <div className="bg-zinc-800 border border-zinc-700 p-4 mb-4 rounded">
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      className="flex items-center mb-3"
                    >
                      <div className="flex-grow">
                        <Form.Item
                          {...restField}
                          name={name}
                          className="mb-1"
                          rules={[{ required: true, message: 'Please enter benefit' }]}
                        >
                          <Input
                            placeholder="Add a job benefit"
                            className="bg-zinc-700 border-zinc-600 !text-white !font-['Barlow']"
                          />
                        </Form.Item>
                      </div>
                      <Button
                        type="text"
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                        className="text-[#ff4d4f] ml-2"
                      />
                    </div>
                  ))}

                  {/* Campo para adicionar benefício personalizado */}
                  <div className="flex items-center mb-4">
                    <Input
                      placeholder="Type a new benefit..."
                      value={customBenefit}
                      onChange={(e) => setCustomBenefit(e.target.value)}
                      onPressEnter={() => addCustomBenefit(add)}
                      className="bg-zinc-700 border-zinc-600 !text-white !font-['Barlow'] mr-2"
                    />
                    <PrimaryButton
                      type="primary"
                      icon={
                        <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="#000000" aria-hidden="true">
                          <path d="M482 152h60q8 0 8 8v704q0 8-8 8h-60q-8 0-8-8V160q0-8 8-8z"></path>
                          <path d="M192 474h672q8 0 8 8v60q0 8-8 8H160q-8 0-8-8v-60q0-8 8-8z"></path>
                        </svg>
                      }
                      onClick={() => addCustomBenefit(add)}
                      className="!bg-[#E3AF25] !text-black"
                      disabled={!customBenefit.trim()}
                    >
                      ADD
                    </PrimaryButton>
                  </div>

                  <div className="flex justify-between">
                    <PrimaryButton
                      type="primary"
                      onClick={() => setShowBenefitExamples(!showBenefitExamples)}
                      className="!bg-[#E3AF25] !text-black hover:!opacity-90 !border-none"
                    >
                      {showBenefitExamples ? 'Hide examples' : 'Show examples'}
                    </PrimaryButton>
                  </div>

                  {showBenefitExamples && (
                    <div className="mt-4 p-3 bg-zinc-700 rounded">
                      <div className="text-white mb-2 font-['Barlow']">Examples (click to add):</div>
                      <div className="flex flex-wrap gap-2">
                        {benefitExamples.map((example, idx) => (
                          <Tag
                            key={idx}
                            className="!bg-zinc-600 !text-white cursor-pointer !font-['Barlow'] mb-2"
                            onClick={() => addSpecificBenefit(example, add)}
                          >
                            {example}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Row gutter={16} justify="end">
          <Col>
            <PrimaryButton
              onClick={onCancel}
              className="mr-2 !text-white !font-['Barlow']"
            >
              CANCEL
            </PrimaryButton>
            <PrimaryButton
              onClick={handleSubmit}
              loading={loading}
              className="!text-black !font-['Barlow']"
            >
              {initialValues ? 'UPDATE' : 'SAVE'}
            </PrimaryButton>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
